import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  PriceDocumentInterface,
  SaleItemInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { priceDocumentValidations } from '../../../../../../common/utils/constants'
import { processAttributes } from '../../../../../../common/utils/functions/processAttributes'
import { PriceDocumentType } from '../../../../../../common/types/enums'

const validations: ValidationRulesInterface[] = priceDocumentValidations

export const useForm = (
  document: Partial<PriceDocumentInterface>,
  setDocument: React.Dispatch<SetStateAction<Partial<PriceDocumentInterface>>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([])
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set())

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const markFieldDirty = useCallback((fieldPath: string) => {
    setDirtyFields((prev) => new Set([...prev, fieldPath]))
  }, [])

  const { inputHandler } = useInput(setDocument)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setSaleItemList(result?.data || [])
    } catch (error) {
      console.error('Error loading items:', error)
    }
  }, [salesApi])

  const createSaleItemSelectList = useCallback(() => {
    return saleItemList.map((item) => ({
      id: String(item.id),
      label:
        '#' +
        item.id +
        ' ' +
        item.name +
        (item.attributes?.length ? ' ' + processAttributes(item.attributes) : ''),
      name: item.name,
      value: String(item.id),
    }))
  }, [saleItemList])

  const handlePriceChange = (index: number, field: string, value: string | number): void => {
    if (field === 'price') {
      const strValue = String(value)
      let filtered = strValue.replace(/\D/g, '')
      filtered = filtered.replace(/^0+/, '')
      if (filtered.length > 7) filtered = filtered.slice(0, 7)

      setDocument((prev) => ({
        ...prev,
        prices: prev.prices?.map((item, i) =>
          i === index
            ? {
                ...item,
                price: filtered ? Number(filtered) : 0, // ✅ приводим к number
              }
            : item,
        ),
      }))
    } else {
      setDocument((prev) => ({
        ...prev,
        prices: prev.prices?.map((item, i) =>
          i === index
            ? {
                ...item,
                [field]: value,
              }
            : item,
        ),
      }))
    }
  }

  const getItemValidationResult = useCallback(
    (index: number, field: 'saleItemId' | 'price') => {
      const value = document.prices?.[index]?.[field]
      const fieldPath = `prices[${index}].${field}`

      if (!value) {
        return {
          name: fieldPath,
          isEmptyError: true,
          isDirty: false,
          isEmpty: true,
          notValidFormat: false,
          tooShort: false,
          tooLong: false,
          notValid: false,
        } as ValidationResultInterface
      }

      return undefined
    },
    [document.prices],
  )

  const formAction = useCallback(async () => {
    if (!isFormValid) return

    try {
      await showCommonLoader()
      await delayAction(1000)

      const payload = {
        number: document.number?.trim() || '',
        comment: document.comment || '',
        prices:
          document.prices?.map((p) => ({
            saleItemId: Number(p.saleItemId),
            price: Number(p.price),
          })) || [],
      }

      if (edit && document.id) {
        // await salesApi.updatePriceDocument(document.id, payload)
      } else {
        await salesApi.createPriceDocument({ ...payload, type: PriceDocumentType.MANUAL })
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [
    isFormValid,
    document,
    edit,
    salesApi,
    getList,
    setModalActive,
    showCommonLoader,
    hideCommonLoader,
  ])

  useEffect(() => {
    getSaleItemList()
  }, [getSaleItemList])

  useEffect(() => {
    for (const key in document) {
      if (validations.some((v) => v.fieldName === key)) {
        checkAndAddValidation(key)
      }
    }
  }, [document, checkAndAddValidation])

  useEffect(() => {
    for (const key in document) {
      if (validations.some((v) => v.fieldName === key)) {
        validate(key, document[key])
      }
    }
  }, [document, validate])

  useEffect(() => {
    const hasErrors = validationResult.some((el) => el.notValid)
    const hasPrices = document.prices && document.prices.length > 0
    const pricesValid =
      document.prices?.every((p) => p.saleItemId && p.price && Number(p.price) > 0) ?? false

    setIsFormValid(Boolean(!hasErrors && hasPrices && pricesValid))
  }, [validationResult, document.prices])

  return {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    createSaleItemSelectList,
    disabled: !isFormValid,
    handlePriceChange,
    getItemValidationResult,
    markFieldDirty,
  }
}
